import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';
import { useResizeOnNextFrame } from 'lib/hooks/useResizeOnNextFrame';

import { countFitting } from '../../MoreMenu/lib/fitItems';
import { emptyFilter, pickSuggestedFilters, suggestionChange } from '../lib/suggestedFilters';

import AppliedFilters from './AppliedFilters';
import FilterValueTrigger from './FilterValueTrigger';

const GAP = 8;
const MENU = '__menu';
const ADD_CHIP =
  '!rounded-md !border !border-dashed !border-neutral-300 !bg-white hover:!bg-neutral-50 [&_*]:!text-neutral-600';

const addTrigger =
  (filter, position, updateFilters) =>
  ({ values, onChange, data, index }) => (
    <FilterValueTrigger
      type={filter.type}
      values={values}
      onChange={onChange}
      data={data}
      index={index}
      position={position}
      onWrongFilter={updateFilters}
      className={ADD_CHIP}
      icon="add-mono"
      text={filter.title}
    />
  );

// Each chip is the regular filter chip with its own picker, so a pick behaves exactly like an
// applied filter. The menu always keeps its room, so it never slides under the row's edge.
const SuggestedFilters = ({
  defaultFilters,
  filters,
  changeFilters,
  updateFilters,
  limit,
  quickKeys,
  exclude,
  position,
  menuKeys = [],
  renderMenu
}) => {
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const widthsRef = useRef({});
  const menuKeysRef = useRef(menuKeys);
  menuKeysRef.current = menuKeys;

  const quickSignature = Array.isArray(quickKeys) ? quickKeys.join('|') : null;
  const excludeSignature = (exclude || []).join('|');
  const suggested = useMemo(
    () => pickSuggestedFilters(defaultFilters, filters, limit, { quickKeys, exclude }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultFilters, filters, limit, quickSignature, excludeSignature]
  );
  const [fitCount, setFitCount] = useState(suggested.length);
  const [menuWidth, setMenuWidth] = useState(0);
  const keys = suggested.map(([key]) => key);
  const signature = keys.join('|');
  const menuSignature = menuKeys.join('|');
  const menu = renderMenu?.(keys.slice(0, fitCount), keys.length > 0);
  const hasMenu = !!menu;

  const fit = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const chips = Array.from(container.querySelectorAll(':scope > [data-chip]'));
    chips.forEach((el) => {
      widthsRef.current[el.dataset.chip] = el.offsetWidth;
    });
    if (menuRef.current) widthsRef.current[MENU] = menuRef.current.offsetWidth;
    const measuredMenu = widthsRef.current[MENU] ?? 0;
    const chipKeys = chips.map((el) => el.dataset.chip);
    // The menu needs its room while it still holds a filter no visible chip offers.
    const needsMenu = (count) =>
      menuKeysRef.current.some((key) => !chipKeys.slice(0, count).includes(key));

    setFitCount(
      countFitting({
        widths: chipKeys.map((key) => widthsRef.current[key]),
        availableWidth: container.clientWidth,
        gap: GAP,
        reserve: (count) => (needsMenu(count) ? GAP + measuredMenu : 0)
      })
    );
    setMenuWidth(measuredMenu);
  }, []);

  useLayoutEffect(fit, [fit, signature, menuSignature, hasMenu]);
  useResizeOnNextFrame(containerRef, fit);

  if (!suggested.length && !menu) return null;

  return (
    <div
      ref={containerRef}
      style={hasMenu && menuWidth ? { minWidth: menuWidth } : undefined}
      className="relative flex min-w-0 flex-1 basis-0 items-start gap-2">
      {suggested.map(([key, filter], index) => {
        const empty = emptyFilter(filter);
        return (
          <div
            key={key}
            data-chip={key}
            aria-hidden={index >= fitCount || undefined}
            // A chip that doesn't fit stays measurable but out of the way, so it can come back.
            className={cn(
              'relative flex w-max shrink-0',
              index >= fitCount && 'pointer-events-none invisible absolute left-0 top-0'
            )}>
            <AppliedFilters
              filters={{ [key]: [empty] }}
              defaultFilters={defaultFilters}
              changeFilters={suggestionChange(changeFilters, key, empty)}
              updateFilters={updateFilters}
              position={position}
              hideOperator
              clearable
              valueRenderers={{ [key]: addTrigger(filter, position, updateFilters) }}
            />
          </div>
        );
      })}
      {menu && (
        <div ref={menuRef} className="flex shrink-0">
          {menu}
        </div>
      )}
    </div>
  );
};

export default SuggestedFilters;
